import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';

// ── Constants ─────────────────────────────────────────────────
const GAMES = [
  {
    id: 'crucible',
    name: 'Crucible',
    tagline: 'Prepare for battle, but embrace the chaos.',
    sub: 'Homework ×2 · Shenanigans ×2',
    color: '#c98a18',
    description: 'You did the work. Now survive it. Bring two prepared ideas, build them under fire, chaos-session two more, then choose one to finish together.',
  },
  {
    id: 'gauntlet',
    name: 'Gauntlet',
    tagline: 'Sequential, relentless, finish before you move on.',
    sub: '2 days · 3 songs · 80%+ complete',
    color: '#7b4fc4',
    description: 'Nobody moves until everybody\'s ready. Three songs. All the way through. Zero mercy for unfinished business.',
  },
  {
    id: 'primal',
    name: 'Primal',
    tagline: 'Back to the basics, just instinct and four elements.',
    sub: 'Topline · Percussion · Chords · Bass (opt.) · As many kills as you can',
    color: '#1a9080',
    description: 'Strip everything. Melody, lyrics, chords, percussion. Nothing more. How many complete songs can you forge in 48 hours?',
  },
];

const VARIANTS = [
  { id: 'muses',      name: 'Summon the Muses',         tagline: 'invoke',                              color: '#a87ee8' },
  { id: 'mercenary',  name: 'The Mercenary',             tagline: 'one session. one weapon. no mercy.',  color: '#e8621a' },
  { id: 'medicine',   name: 'Medicine Diskbros',         tagline: 'no explanation needed',               color: '#c98a18' },
  { id: 'challenge',  name: 'Challenge Accepted',        tagline: 'too late, just enjoy',                color: '#e84040' },
  { id: 'location',   name: 'Location, Location, Location', tagline: 'whereto, boys?',                  color: '#4a9eed' },
  { id: 'silent',     name: 'Silent (Re)treatment',      tagline: 'shhhhh, it\'s ok bby',               color: '#6a8a9a' },
];

const PLAYERS = ['AhziRa', 'Avidx', 'Crash', 'DJ Schmalec'];

const ROLES = ['Producer', 'Songwriter', 'Both'];

const REWARD_OPTIONS = [
  { id: 'us_trip',       name: 'US-based Trip',          emoji: '🏔️' },
  { id: 'intl_trip',     name: 'International Retreat',  emoji: '✈️' },
  { id: 'gear',          name: 'Gear Fund',               emoji: '🎸' },
  { id: 'show',          name: 'Book a Show',             emoji: '🎤' },
  { id: 'plugins',       name: 'Plug-ins',                emoji: '🎹' },
  { id: 'wildcard',      name: 'Wild Card',               emoji: '🎰' },
];

const STAGES = ['Seed', 'Sprout', 'Plant', 'Tree', 'Tesseræ'];
const SESSION_DURATION = 48 * 60 * 60 * 1000; // 48 hours in ms

// ── War Games Logo SVG ────────────────────────────────────────
function WarGamesLogo({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <ellipse cx="24" cy="30" rx="10" ry="12" fill="url(#logoFlame)" opacity="0.4" />
      {/* Left sword */}
      <line x1="14" y1="4" x2="22" y2="26" stroke="#e8621a" strokeWidth="2.5" strokeLinecap="round" />
      <polygon points="12,2 16,2 14,6" fill="#e8621a" />
      <line x1="11" y1="22" x2="17" y2="22" stroke="#e8621a" strokeWidth="2" strokeLinecap="round" />
      {/* Right sword */}
      <line x1="34" y1="4" x2="26" y2="26" stroke="#e8621a" strokeWidth="2.5" strokeLinecap="round" />
      <polygon points="32,2 36,2 34,6" fill="#e8621a" />
      <line x1="31" y1="22" x2="37" y2="22" stroke="#e8621a" strokeWidth="2" strokeLinecap="round" />
      {/* Center sword */}
      <line x1="24" y1="2" x2="24" y2="27" stroke="#f0a030" strokeWidth="3" strokeLinecap="round" />
      <polygon points="22,1 26,1 24,5" fill="#f0a030" />
      <line x1="21" y1="24" x2="27" y2="24" stroke="#f0a030" strokeWidth="2.5" strokeLinecap="round" />
      {/* Flame */}
      <path d="M24 48 C20 43 16 38 18 32 C20 28 24 30 24 34 C24 30 28 26 30 30 C32 36 28 43 24 48Z" fill="url(#logoFlame)" opacity="0.9" />
      <defs>
        <radialGradient id="logoFlame" cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor="#f0a030" />
          <stop offset="60%" stopColor="#e8621a" />
          <stop offset="100%" stopColor="#e8621a" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

// ── Countdown Timer ───────────────────────────────────────────
function Countdown({ startedAt, onExpire }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const left = Math.max(0, SESSION_DURATION - elapsed);
      setRemaining(left);
      if (left === 0 && onExpire) onExpire();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, onExpire]);

  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  const pct = remaining / SESSION_DURATION;
  const urgent = pct < 0.1;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 6 }}>Time Remaining</div>
      <div style={{ fontSize: 32, fontWeight: 900, fontFamily: 'var(--mono)', color: urgent ? '#e84040' : '#f0a030', letterSpacing: 2, transition: 'color .5s' }}>
        {String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}
      </div>
      <div style={{ marginTop: 8, height: 3, borderRadius: 2, background: 'var(--surface3)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct * 100}%`, background: urgent ? '#e84040' : 'linear-gradient(90deg,#f0a030,#e8621a)', transition: 'width 1s linear, background .5s' }} />
      </div>
    </div>
  );
}

// ── Song Card (end game) ──────────────────────────────────────
function SongCard({ song, onChange, onRemove }) {
  return (
    <div style={{ padding: 16, borderRadius: 12, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input value={song.title} onChange={e => onChange({ ...song, title: e.target.value })}
          placeholder="Song title..." style={{ flex: 1, fontWeight: 700, fontSize: 14 }} />
        <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 4 }}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Stage (Tree = 80%+)</label>
          <select value={song.stage} onChange={e => onChange({ ...song, stage: e.target.value })}>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Artist</label>
          <input value={song.artist} onChange={e => onChange({ ...song, artist: e.target.value })} placeholder="AhziRa / Avidx..." />
        </div>
      </div>
    </div>
  );
}

// ── End Game Modal ────────────────────────────────────────────
function EndGameModal({ session, onClose, onConfirm }) {
  const game = GAMES.find(g => g.id === session.gameId);
  const [songs, setSongs] = useState(
    (session.songs || [{ id: '1', title: '', stage: 'Seed', artist: '' }])
  );

  const addSong = () => setSongs(s => [...s, { id: Date.now().toString(), title: '', stage: 'Seed', artist: '' }]);
  const updateSong = (id, data) => setSongs(s => s.map(x => x.id === id ? data : x));
  const removeSong = (id) => setSongs(s => s.filter(x => x.id !== id));

  // Score calculation
  const calcScore = () => {
    const completed = songs.filter(s => s.stage === 'Tree' || s.stage === 'Tesseræ');
    if (session.gameId === 'crucible') {
      if (completed.length === 4) return 150;
      if (completed.length === 3) return 100;
      if (completed.length === 2) return 50;
      if (completed.length === 1) return 10;
      return 0;
    }
    if (session.gameId === 'gauntlet') {
      if (songs.length > 0 && completed.length === 0) return 0;
      if (completed.length === 3) return 100;
      if (completed.length === 2) return 50;
      if (completed.length === 1) return 10;
      return 0;
    }
    if (session.gameId === 'primal') return completed.length * 12.5;
    return 0;
  };

  const score = calcScore();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: 32, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <WarGamesLogo size={40} />
          <div style={{ fontSize: 22, fontWeight: 900, marginTop: 12 }}>War Games Complete</div>
          <div style={{ fontSize: 13, color: game?.color, fontFamily: 'var(--mono)', fontWeight: 700, marginTop: 4 }}>{game?.name?.toUpperCase()}</div>
        </div>

        {/* Score preview */}
        <div style={{ padding: '16px 20px', borderRadius: 12, background: `${game?.color}18`, border: `1px solid ${game?.color}44`, marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 6 }}>Session Score</div>
          <div style={{ fontSize: 48, fontWeight: 900, color: game?.color, fontFamily: 'var(--mono)' }}>{score}<span style={{ fontSize: 20 }}>pts</span></div>
        </div>

        {/* Songs */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 12 }}>Songs Created</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {songs.map(song => (
              <SongCard key={song.id} song={song} onChange={data => updateSong(song.id, data)} onRemove={() => removeSong(song.id)} />
            ))}
          </div>
          <button onClick={addSong} style={{ marginTop: 10, width: '100%', padding: '10px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--dim)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--mono)' }}>
            + Add Song
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button onClick={() => onConfirm(songs, score)}
            style={{ padding: '10px 24px', borderRadius: 8, background: `linear-gradient(135deg,${game?.color},${game?.color}88)`, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            ⚔️ End Session
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Active Session Console ────────────────────────────────────
function ActiveSession({ session, onUpdate, onEnd }) {
  const game = GAMES.find(g => g.id === session.gameId);
  const [showEndModal, setShowEndModal] = useState(false);
  const [expired, setExpired] = useState(false);

  const handleExpire = () => {
    setExpired(true);
    setShowEndModal(true);
  };

  const handleEndConfirm = async (songs, score) => {
    await onEnd(songs, score);
    setShowEndModal(false);
  };

  const completedSongs = (session.songs || []).filter(s => s.stage === 'Tree' || s.stage === 'Tesseræ');

  return (
    <div style={{ padding: '28px 30px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <WarGamesLogo size={36} />
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)' }}>War Games · Active</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: game?.color }}>{game?.name}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>{game?.tagline}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => setShowEndModal(true)}
            style={{ padding: '10px 20px', borderRadius: 8, background: 'rgba(232,98,26,.15)', border: '1px solid rgba(232,98,26,.4)', color: '#e8621a', fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: 'var(--mono)' }}>
            ⚔️ End Game
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Countdown */}
        <div style={{ padding: 24, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Countdown startedAt={session.startedAt} onExpire={handleExpire} />
        </div>

        {/* Players */}
        <div style={{ padding: 24, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 12 }}>War Party</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {session.players.map(p => (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{p.name[0]}</div>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', padding: '2px 8px', borderRadius: 20, background: 'var(--surface2)', border: '1px solid var(--border)' }}>{p.role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Variants active */}
      {session.variants?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {session.variants.map(vId => {
            const v = VARIANTS.find(x => x.id === vId);
            return v ? (
              <div key={vId} style={{ padding: '4px 12px', borderRadius: 20, background: `${v.color}18`, border: `1px solid ${v.color}44`, fontSize: 11, color: v.color, fontFamily: 'var(--mono)', fontWeight: 600 }}>
                {v.name}
              </div>
            ) : null;
          })}
        </div>
      )}

      {/* Game-specific phase tracker */}
      {session.gameId === 'crucible' && <CrucibleTracker session={session} onUpdate={onUpdate} />}
      {session.gameId === 'gauntlet' && <GauntletTracker session={session} onUpdate={onUpdate} />}
      {session.gameId === 'primal' && <PrimalTracker session={session} onUpdate={onUpdate} />}

      {showEndModal && <EndGameModal session={session} onClose={() => !expired && setShowEndModal(false)} onConfirm={handleEndConfirm} />}
    </div>
  );
}

// ── Crucible Phase Tracker ────────────────────────────────────
function CrucibleTracker({ session, onUpdate }) {
  const phases = [
    { id: 'day0',   label: 'Day 0',   desc: 'Pick 4 starter ideas. Group chooses 2 for homework.' },
    { id: 'day1',   label: 'Day 1',   desc: 'Build the 2 homework songs. Deep work.' },
    { id: 'night1', label: 'Night 1', desc: 'Shenanigans. Free for all. Seed 2 more ideas.' },
    { id: 'day2',   label: 'Day 2',   desc: 'Build the Night 1 seeds. Deep work.' },
    { id: 'night2', label: 'Night 2', desc: 'Vote on one song. Finish it together.' },
  ];
  const currentPhase = session.phase || 'day0';

  return (
    <div style={{ padding: 24, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>Phase Tracker</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {phases.map((phase, idx) => {
          const phaseIdx = phases.findIndex(p => p.id === currentPhase);
          const isDone = idx < phaseIdx;
          const isActive = phase.id === currentPhase;
          return (
            <div key={phase.id} onClick={() => onUpdate({ phase: phase.id })}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: isActive ? 'rgba(201,138,24,.1)' : 'var(--surface2)', border: `1px solid ${isActive ? 'rgba(201,138,24,.4)' : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: isDone ? '#27ae60' : isActive ? '#c98a18' : 'var(--surface3)', border: `1px solid ${isDone ? '#27ae60' : isActive ? '#c98a18' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, color: isDone || isActive ? '#fff' : 'var(--dim)' }}>
                {isDone ? '✓' : idx + 1}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#c98a18' : isDone ? 'var(--muted)' : 'var(--text)' }}>{phase.label}</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>{phase.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Gauntlet Tracker ──────────────────────────────────────────
function GauntletTracker({ session, onUpdate }) {
  const songs = session.gauntletSongs || [
    { id: '1', title: 'Song 1', pct: 0, producerDone: false },
    { id: '2', title: 'Song 2', pct: 0, producerDone: false },
    { id: '3', title: 'Song 3', pct: 0, producerDone: false },
  ];

  const updateSong = (id, data) => {
    const updated = songs.map(s => s.id === id ? { ...s, ...data } : s);
    onUpdate({ gauntletSongs: updated });
  };

  return (
    <div style={{ padding: 24, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>The Queue</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {songs.map((song, idx) => {
          const prevDone = idx === 0 || (songs[idx - 1]?.pct >= 80);
          const locked = !prevDone;
          return (
            <div key={song.id} style={{ padding: '16px 18px', borderRadius: 12, background: locked ? 'var(--surface2)' : 'var(--surface)', border: `1px solid ${song.pct >= 80 ? '#27ae60' : locked ? 'var(--border)' : '#7b4fc444'}`, opacity: locked ? 0.5 : 1, transition: 'all .2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: song.pct >= 80 ? '#27ae60' : locked ? 'var(--surface3)' : '#7b4fc4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff' }}>{idx + 1}</div>
                <input value={song.title} disabled={locked} onChange={e => updateSong(song.id, { title: e.target.value })}
                  style={{ flex: 1, background: 'transparent', border: 'none', fontWeight: 700, fontSize: 14, color: 'var(--text)', outline: 'none', fontFamily: 'var(--font)' }} />
                {song.pct >= 80 && <span style={{ fontSize: 10, color: '#27ae60', fontFamily: 'var(--mono)', fontWeight: 700 }}>✓ 80%+</span>}
                {locked && <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>🔒 locked</span>}
              </div>
              {!locked && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <input type="range" min={0} max={100} value={song.pct} onChange={e => updateSong(song.id, { pct: Number(e.target.value) })}
                      style={{ flex: 1, accentColor: '#7b4fc4' }} />
                    <span style={{ fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 700, color: song.pct >= 80 ? '#27ae60' : '#7b4fc4', minWidth: 36 }}>{song.pct}%</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Primal Tracker ────────────────────────────────────────────
function PrimalTracker({ session, onUpdate }) {
  const kills = session.primalKills || [];

  const addKill = () => {
    onUpdate({ primalKills: [...kills, { id: Date.now().toString(), title: '', done: false }] });
  };

  const updateKill = (id, data) => {
    onUpdate({ primalKills: kills.map(k => k.id === id ? { ...k, ...data } : k) });
  };

  const removeKill = (id) => {
    onUpdate({ primalKills: kills.filter(k => k.id !== id) });
  };

  const doneCount = kills.filter(k => k.done).length;

  return (
    <div style={{ padding: 24, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)' }}>Kill Count</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#1a9080', fontFamily: 'var(--mono)' }}>{doneCount} <span style={{ fontSize: 11, color: 'var(--dim)' }}>kills · {(doneCount * 12.5).toFixed(1)}pts</span></div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {kills.map(kill => (
          <div key={kill.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: kill.done ? 'rgba(26,144,128,.1)' : 'var(--surface2)', border: `1px solid ${kill.done ? '#1a908044' : 'var(--border)'}` }}>
            <button onClick={() => updateKill(kill.id, { done: !kill.done })}
              style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${kill.done ? '#1a9080' : 'var(--border)'}`, background: kill.done ? '#1a9080' : 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10 }}>
              {kill.done ? '✓' : ''}
            </button>
            <input value={kill.title} onChange={e => updateKill(kill.id, { title: e.target.value })} placeholder="Song title..."
              style={{ flex: 1, background: 'transparent', border: 'none', fontSize: 13, color: kill.done ? '#1a9080' : 'var(--text)', outline: 'none', fontFamily: 'var(--font)', textDecoration: kill.done ? 'none' : 'none' }} />
            <button onClick={() => removeKill(kill.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', fontSize: 12 }}>✕</button>
          </div>
        ))}
      </div>
      <button onClick={addKill} style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--dim)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--mono)' }}>
        + New Kill
      </button>
    </div>
  );
}

// ── Session Setup ─────────────────────────────────────────────
function SessionSetup({ onLaunch, onCancel }) {
  const [step, setStep] = useState(0); // 0=players, 1=game, 2=variants
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [players, setPlayers] = useState([{ name: 'AhziRa', role: 'Both' }, { name: 'Avidx', role: 'Songwriter' }]);
  const [hoveredGame, setHoveredGame] = useState(null);

  const toggleVariant = (id) => setSelectedVariants(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]);
  const togglePlayer = (name) => {
    if (players.find(p => p.name === name)) {
      if (players.length > 2) setPlayers(p => p.filter(x => x.name !== name));
    } else {
      if (players.length < 4) setPlayers(p => [...p, { name, role: 'Both' }]);
    }
  };
  const updateRole = (name, role) => setPlayers(p => p.map(x => x.name === name ? { ...x, role } : x));

  const game = GAMES.find(g => g.id === selectedGame);

  return (
    <div style={{ padding: '28px 30px', maxWidth: 700, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <WarGamesLogo size={32} />
        <div>
          <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)' }}>War Games</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>New Session</div>
        </div>
        <button onClick={onCancel} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', fontSize: 20 }}>✕</button>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
        {['War Party', 'Game', 'Variants'].map((label, i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? '#e8621a' : 'var(--surface3)', transition: 'background .2s' }} />
        ))}
      </div>

      {/* Step 0: Players */}
      {step === 0 && (
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>Assemble the War Party <span style={{ color: 'var(--dim)', fontWeight: 400 }}>(2–4 players)</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {PLAYERS.map(name => {
              const inParty = players.find(p => p.name === name);
              return (
                <div key={name} style={{ padding: '14px 18px', borderRadius: 12, background: inParty ? 'rgba(123,79,196,.1)' : 'var(--surface)', border: `1px solid ${inParty ? 'rgba(123,79,196,.4)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all .15s' }}
                  onClick={() => togglePlayer(name)}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${inParty ? '#7b4fc4' : 'var(--border)'}`, background: inParty ? '#7b4fc4' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>
                    {inParty ? '✓' : ''}
                  </div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{name}</div>
                  {inParty && (
                    <select value={inParty.role} onChange={e => { e.stopPropagation(); updateRole(name, e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                      {ROLES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button disabled={players.length < 2} onClick={() => setStep(1)} className="btn btn-primary">Next: Choose Game →</button>
          </div>
        </div>
      )}

      {/* Step 1: Choose game */}
      {step === 1 && (
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>Choose Your Game</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {GAMES.map(g => (
              <div key={g.id}
                onClick={() => setSelectedGame(g.id)}
                onMouseEnter={() => setHoveredGame(g.id)}
                onMouseLeave={() => setHoveredGame(null)}
                style={{ padding: '20px 24px', borderRadius: 14, background: selectedGame === g.id ? `${g.color}15` : 'var(--surface)', border: `1px solid ${selectedGame === g.id ? g.color : hoveredGame === g.id ? `${g.color}66` : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: selectedGame === g.id ? g.color : 'var(--text)', marginBottom: 4 }}>{g.name}</div>
                    <div style={{ fontSize: 11, color: g.color, fontFamily: 'var(--mono)', fontWeight: 600, marginBottom: 6 }}>{g.tagline}</div>
                    <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{g.sub}</div>
                  </div>
                  {selectedGame === g.id && <div style={{ fontSize: 18 }}>⚔️</div>}
                </div>
                {(hoveredGame === g.id || selectedGame === g.id) && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${g.color}33` }}>
                    {g.description}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button onClick={() => setStep(0)} className="btn btn-ghost">← Back</button>
            <button disabled={!selectedGame} onClick={() => setStep(2)} className="btn btn-primary">Next: Variants →</button>
          </div>
        </div>
      )}

      {/* Step 2: Variants */}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>Select Variants <span style={{ color: 'var(--dim)', fontWeight: 400 }}>(optional)</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {VARIANTS.map(v => (
              <div key={v.id} onClick={() => toggleVariant(v.id)}
                style={{ padding: '14px 16px', borderRadius: 12, background: selectedVariants.includes(v.id) ? `${v.color}18` : 'var(--surface)', border: `1px solid ${selectedVariants.includes(v.id) ? v.color : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: selectedVariants.includes(v.id) ? v.color : 'var(--text)', marginBottom: 3 }}>{v.name}</div>
                <div style={{ fontSize: 10, color: 'var(--dim)', fontStyle: 'italic' }}>{v.tagline}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => setStep(1)} className="btn btn-ghost">← Back</button>
            <button
              onClick={() => onLaunch({ gameId: selectedGame, variants: selectedVariants, players })}
              style={{ padding: '10px 28px', borderRadius: 8, background: `linear-gradient(135deg,${game?.color},${game?.color}88)`, border: 'none', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
              ⚔️ Let the Games Begin
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── History of War ────────────────────────────────────────────
function HistoryOfWar({ history }) {
  if (!history.length) return (
    <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
      No sessions yet. The war has not begun.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {history.map(session => {
        const game = GAMES.find(g => g.id === session.gameId);
        const date = session.endedAt ? new Date(session.endedAt).toLocaleDateString() : '—';
        return (
          <div key={session.id} style={{ padding: '14px 18px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: game?.color || 'var(--muted)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{game?.name} <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{date}</span></div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                {session.players?.map(p => p.name).join(' · ')}
                {session.variants?.length > 0 && ` · ${session.variants.length} variant${session.variants.length > 1 ? 's' : ''}`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: game?.color, fontFamily: 'var(--mono)' }}>{session.score || 0}</div>
              <div style={{ fontSize: 9, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>PTS</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Campaign Snapshot Modal ───────────────────────────────────
function CampaignSnapshot({ session, onClose }) {
  const game = GAMES.find(g => g.id === session.gameId);
  const date = session.endedAt ? new Date(session.endedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';
  const buyIn = (session.players?.length || 2) * 30;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--surface)', border: `1px solid ${game?.color}44`, borderRadius: 20, padding: 32, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <WarGamesLogo size={32} />
          <div>
            <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)' }}>Campaign Snapshot</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: game?.color }}>{game?.name}</div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', fontSize: 18 }}>✕</button>
        </div>

        {/* Meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 4 }}>Score</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: game?.color, fontFamily: 'var(--mono)' }}>{session.score || 0}</div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 4 }}>Buy-in</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#f0a030', fontFamily: 'var(--mono)' }}>${buyIn}</div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 4 }}>Date</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 4 }}>{date}</div>
          </div>
        </div>

        {/* War Party */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 10 }}>War Party</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {session.players?.map(p => (
              <div key={p.name} style={{ padding: '4px 12px', borderRadius: 20, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 11, fontWeight: 600 }}>
                {p.name} <span style={{ color: 'var(--dim)', fontWeight: 400 }}>· {p.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Variants */}
        {session.variants?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 10 }}>Variants</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {session.variants.map(vId => {
                const v = VARIANTS.find(x => x.id === vId);
                return v ? <div key={vId} style={{ padding: '4px 12px', borderRadius: 20, background: `${v.color}18`, border: `1px solid ${v.color}44`, fontSize: 11, color: v.color, fontWeight: 600 }}>{v.name}</div> : null;
              })}
            </div>
          </div>
        )}

        {/* Songs */}
        {session.songs?.length > 0 && (
          <div>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 10 }}>Songs</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {session.songs.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{s.title || 'Untitled'}</div>
                  <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: s.stage === 'Tree' || s.stage === 'Tesseræ' ? 'rgba(39,174,96,.15)' : 'var(--surface3)', color: s.stage === 'Tree' || s.stage === 'Tesseræ' ? '#27ae60' : 'var(--dim)', fontFamily: 'var(--mono)', fontWeight: 600 }}>{s.stage}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── War Chest ─────────────────────────────────────────────────
function WarChest({ chest, totalPoints, history, onAddReward }) {
  const [expanded, setExpanded] = useState(false);
  const [showRewardPicker, setShowRewardPicker] = useState(false);
  const [customReward, setCustomReward] = useState('');
  const [viewingCampaign, setViewingCampaign] = useState(null);

  const allRewards = [...REWARD_OPTIONS, ...(chest.customRewards || [])];

  return (
    <>
      <div style={{ borderRadius: 16, background: 'var(--surface)', border: '1px solid rgba(201,138,24,.3)', overflow: 'hidden' }}>
        {/* Collapsed header — points prominent, cash secondary */}
        <div onClick={() => setExpanded(v => !v)}
          style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}>
          <span style={{ fontSize: 20 }}>⚔️</span>
          <div>
            <div style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: '#c98a18', fontFamily: 'var(--mono)' }}>War Chest</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#f0a030', fontFamily: 'var(--mono)', lineHeight: 1.1 }}>{totalPoints}<span style={{ fontSize: 12, color: 'var(--dim)', marginLeft: 4 }}>pts</span></div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--dim)', marginLeft: 'auto' }}>{expanded ? '▲' : '▼'}</div>
        </div>

        {/* Expanded */}
        {expanded && (
          <div style={{ borderTop: '1px solid rgba(201,138,24,.2)', padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)' }}>Cash Balance</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#f0a030', fontFamily: 'var(--mono)' }}>${chest.balance || 0}</div>
            </div>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 12 }}>Previous Campaigns</div>
            {history.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 16 }}>No campaigns yet. The war has not begun.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {history.map(session => {
                const game = GAMES.find(g => g.id === session.gameId);
                const date = session.endedAt ? new Date(session.endedAt).toLocaleDateString() : '—';
                return (
                  <div key={session.id} onClick={() => setViewingCampaign(session)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = game?.color || 'var(--border-active)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 4, background: game?.color || 'var(--dim)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{game?.name} <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{date}</span></div>
                      <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{session.players?.map(p => p.name).join(' · ')}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 900, color: game?.color, fontFamily: 'var(--mono)' }}>{session.score || 0}pts</div>
                      <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>${(session.players?.length || 2) * 30}</div>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--dim)' }}>›</div>
                  </div>
                );
              })}
            </div>

            {/* Choose Reward */}
            <button onClick={() => setShowRewardPicker(v => !v)}
              style={{ width: '100%', padding: '10px', borderRadius: 10, background: 'rgba(201,138,24,.12)', border: '1px solid rgba(201,138,24,.3)', color: '#c98a18', fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'var(--mono)', marginBottom: showRewardPicker ? 16 : 0 }}>
              {showRewardPicker ? '▲ Hide Rewards' : '🏆 Choose a Reward'}
            </button>

            {showRewardPicker && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                  {allRewards.map(r => (
                    <div key={r.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = '#c98a18'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{r.emoji || '🎯'}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', lineHeight: 1.3 }}>{r.name}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', marginBottom: 8 }}>Unanimous vote required to spend.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={customReward} onChange={e => setCustomReward(e.target.value)}
                    placeholder="Add custom reward..." style={{ flex: 1, fontSize: 12 }} />
                  <button onClick={() => { if (customReward.trim()) { onAddReward(customReward.trim()); setCustomReward(''); } }}
                    disabled={!customReward.trim()} className="btn btn-ghost btn-sm">+ Add</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {viewingCampaign && <CampaignSnapshot session={viewingCampaign} onClose={() => setViewingCampaign(null)} />}
    </>
  );
}

// ── Main Game Console ─────────────────────────────────────────
export default function GameConsole() {
  const { profile } = useAuth();
  const [view, setView] = useState('home'); // home | setup | active
  const [activeSession, setActiveSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [chest, setChest] = useState({ balance: 0, customRewards: [] });

  // Load from Firestore
  useEffect(() => {
    const unsub1 = onSnapshot(
      query(collection(db, 'wargames_sessions'), orderBy('startedAt', 'desc')),
      snap => {
        const sessions = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const active = sessions.find(s => s.status === 'active');
        const ended = sessions.filter(s => s.status === 'ended');
        setActiveSession(active || null);
        setHistory(ended);
        if (active) setView('active');
      }
    );
    const unsub2 = onSnapshot(doc(db, 'wargames_meta', 'chest'), snap => {
      if (snap.exists()) setChest(snap.data());
    });
    return () => { unsub1(); unsub2(); };
  }, []);

  const totalPoints = history.reduce((acc, s) => acc + (s.score || 0), 0);

  const handleLaunch = async (config) => {
    const ref = await addDoc(collection(db, 'wargames_sessions'), {
      ...config,
      status: 'active',
      startedAt: Date.now(),
      phase: 'day0',
      songs: [],
      createdBy: profile?.email,
      createdAt: serverTimestamp(),
    });
    setView('active');
  };

  const handleUpdate = async (updates) => {
    if (!activeSession) return;
    await updateDoc(doc(db, 'wargames_sessions', activeSession.id), updates);
  };

  const handleEnd = async (songs, score) => {
    if (!activeSession) return;
    // Add buy-in to chest
    const buyIn = (activeSession.players?.length || 2) * 30;
    await updateDoc(doc(db, 'wargames_sessions', activeSession.id), {
      status: 'ended',
      endedAt: Date.now(),
      songs,
      score,
    });
    // Update chest
    const chestRef = doc(db, 'wargames_meta', 'chest');
    await updateDoc(chestRef, { balance: (chest.balance || 0) + buyIn }).catch(() =>
      addDoc(collection(db, 'wargames_meta'), { balance: buyIn, customRewards: [] })
    );
    setView('home');
  };

  const handleAddReward = async (name) => {
    const updated = [...(chest.customRewards || []), { id: Date.now().toString(), name, emoji: '🎯' }];
    await updateDoc(doc(db, 'wargames_meta', 'chest'), { customRewards: updated }).catch(() => {});
  };

  if (view === 'setup') return <SessionSetup onLaunch={handleLaunch} onCancel={() => setView('home')} />;
  if (view === 'active' && activeSession) return <ActiveSession session={activeSession} onUpdate={handleUpdate} onEnd={handleEnd} />;

  return (
    <div style={{ padding: '28px 30px' }}>
      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <WarGamesLogo size={64} />
        <div style={{ fontSize: 32, fontWeight: 900, marginTop: 12, letterSpacing: 1 }}>WAR GAMES</div>
        <div style={{ fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--dim)', fontFamily: 'var(--mono)', marginTop: 4 }}>Mojave Retreat · Arena</div>
      </div>

      {/* War Chest + Points */}
      <div style={{ marginBottom: 32 }}>
        <WarChest chest={chest} totalPoints={totalPoints} history={history} onAddReward={handleAddReward} />
      </div>

      {/* Let the Games Begin */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <button onClick={() => setView('setup')}
          style={{ padding: '16px 48px', borderRadius: 12, background: 'linear-gradient(135deg,#e8621a,#f0a030)', border: 'none', color: '#fff', fontWeight: 900, fontSize: 16, cursor: 'pointer', letterSpacing: 1, boxShadow: '0 4px 32px rgba(232,98,26,.4)', transition: 'all .2s' }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 48px rgba(232,98,26,.6)'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 32px rgba(232,98,26,.4)'}>
          ⚔️ &nbsp;Let the Games Begin
        </button>
      </div>
    </div>
  );
}
